import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {TestObjectCollectionId, TestObjectId, TestRunId} from '@webmate-web-core/shared/types/ids';
import {
    FindingSeverity,
    FindingsQueryResult,
    TestObject,
    TestObjectOrigin,
    TestObjectStateSummary,
    TestReportQueryAggregation
} from '@webmate-web-core/shared/types/testmgmt';

export type TestObjectOverviewActivityStatus = 'observed' | 'no_states' | 'status_unavailable';

export interface TestObjectOverviewMetrics {
    latestState?: TestObjectStateSummary;
    stateSummaryError?: boolean;
    findingsCount?: number;
    collectionCount?: number;
    latestFindingAt?: string;
}

export interface TestObjectOverviewListSummary {
    typeLabel: string;
    typeIcon: TestObjectOrigin;
    activityStatus: TestObjectOverviewActivityStatus;
    activityLabel: string;
    activityClass: string;
    canonicalIdentifier?: string;
    lastSeenAt?: string;
    lastSeenLabel: string;
    recentActivityAt?: string;
    recentActivityLabel: string;
    collectionCount: number;
    findingsCount: number;
    hasFindings: boolean;
    hasCapturedState: boolean;
    tertiaryText?: string;
}

export interface TestObjectOverviewEntry extends TestObject {
    overviewSummary: TestObjectOverviewListSummary;
}

export interface TestObjectSeveritySummary {
    severity: FindingSeverity;
    count: number;
    label: string;
}

export type TestObjectRelatedFinding = FindingsQueryResult['items'][number];

export type TestObjectCurrentFindingsAvailability = 'available' | 'no_state' | 'unavailable';

export interface TestObjectFindingsPreview {
    label: string;
    reason: string;
    finding: TestObjectRelatedFinding;
}

export interface TestObjectFindingsSectionViewModel {
    helperText: string;
    currentHeadline: string;
    currentSupportingText?: string;
    currentScopeLabel: string;
    currentBreakdown: TestObjectSeveritySummary[];
    currentEmptyLabel: string;
    currentUnavailable: boolean;
    historicalScopeLabel: string;
    historicalBreakdown: TestObjectSeveritySummary[];
    historicalTotalLabel: string;
    preview?: TestObjectFindingsPreview;
}

export interface TestObjectDetailViewModel {
    title: string;
    typeLabel: string;
    originLabel: string;
    originDescription: string;
    activityStatus: TestObjectOverviewActivityStatus;
    activityLabel: string;
    activityClass: string;
    canonicalIdentifier?: string;
    createdAt?: DateTime;
    updatedAt?: DateTime;
    lastCapturedAt?: DateTime;
    lastCapturedLabel: string;
    collectionsCount: number;
    statesCount: number;
    findingsCount: number;
    sessionsCount: number;
    recentActivityAt?: DateTime;
    recentActivityLabel: string;
    authoredProvenanceLabel: string;
    testRunIds: TestRunId[];
    browserSessionIds: string[];
    collectionIds: TestObjectCollectionId[];
}

const TYPE_LABELS: Record<TestObjectOrigin, string> = {
    unknown: 'Unknown',
    manual: 'Manual',
    url_list: 'URL List',
    crawled: 'Crawled',
    observation_target: 'Observation Target'
};

const ORIGIN_DESCRIPTIONS: Record<TestObjectOrigin, string> = {
    unknown: 'Source not recorded.',
    manual: 'Created manually.',
    url_list: 'Created from a URL list source.',
    crawled: 'Created from crawler output.',
    observation_target: 'Created from observation targets.'
};

const SEVERITY_ORDER: Record<FindingSeverity, number> = {
    error: 0,
    warn: 1,
    info: 2,
    good: 3,
    not_set: 4
};

const SEVERITY_LABELS: Record<FindingSeverity, string> = {
    error: 'Error',
    warn: 'Warn',
    info: 'Info',
    good: 'Good',
    not_set: 'None'
};

export function getTestObjectTypeLabel(origin?: TestObjectOrigin): string {
    return TYPE_LABELS[origin ?? 'unknown'];
}

export function getTestObjectOriginDescription(origin?: TestObjectOrigin): string {
    return ORIGIN_DESCRIPTIONS[origin ?? 'unknown'];
}

export function getTestObjectCanonicalIdentifier(testObject: Pick<TestObject, 'generationSpec' | 'matchingSpec'>): string | undefined {
    const generationSpec = testObject.generationSpec as any;
    if (generationSpec?.type === 'URLGenerationSpec' && typeof generationSpec.url === 'string' && generationSpec.url.trim().length > 0) {
        return generationSpec.url.trim();
    }

    if (generationSpec?.type === 'MobileAppGenerationSpec' && typeof generationSpec.packageId === 'string') {
        return `Mobile package ${generationSpec.packageId}`;
    }

    if (typeof generationSpec?.url === 'string' && generationSpec.url.trim().length > 0) {
        return generationSpec.url.trim();
    }

    const matchingSpec = testObject.matchingSpec as any;
    if (typeof matchingSpec?.urlRegex === 'string' && matchingSpec.urlRegex.trim().length > 0) {
        return matchingSpec.urlRegex.trim();
    }

    return undefined;
}

export function getAuthoredProvenanceLabel(origin?: TestObjectOrigin): string {
    return (origin ?? 'unknown') === 'unknown' ? 'Source not recorded' : 'Source recorded';
}

export function buildTestObjectOverviewEntry(testObject: TestObject, metrics: TestObjectOverviewMetrics = {}): TestObjectOverviewEntry {
    const canonicalIdentifier = getTestObjectCanonicalIdentifier(testObject);
    const activityStatus = getTestObjectActivityStatus(!!metrics.latestState, !!metrics.stateSummaryError);
    const lastSeenAt = metrics.latestState?.creationTime;

    return {
        ...testObject,
        overviewSummary: {
            typeLabel: getTestObjectTypeLabel(testObject.origin),
            typeIcon: testObject.origin ?? 'unknown',
            activityStatus,
            activityLabel: getTestObjectActivityLabel(activityStatus),
            activityClass: getTestObjectActivityClass(activityStatus),
            canonicalIdentifier,
            lastSeenAt,
            lastSeenLabel: getLastSeenLabel(lastSeenAt, activityStatus),
            recentActivityAt: getMostRecentTimestamp([
                lastSeenAt,
                metrics.latestFindingAt,
                testObject.lastUpdateTime,
                testObject.creationTime
            ]),
            recentActivityLabel: buildRecentActivityLabel({
                lastCapturedAt: lastSeenAt,
                latestFindingAt: metrics.latestFindingAt,
                lastUpdatedAt: testObject.lastUpdateTime,
                createdAt: testObject.creationTime
            }),
            collectionCount: metrics.collectionCount ?? 0,
            findingsCount: metrics.findingsCount ?? 0,
            hasFindings: (metrics.findingsCount ?? 0) > 0,
            hasCapturedState: !!metrics.latestState,
            tertiaryText: (testObject.description || '').trim() || canonicalIdentifier || undefined
        }
    };
}

export function buildTestObjectDetailViewModel(input: {
    testObject: TestObject;
    states: TestObjectStateSummary[];
    collections: Array<{collectionId: TestObjectCollectionId}>;
    findings: Array<{createdOn?: DateTime}>;
    findingsCount?: number;
    stateSummaryError?: boolean;
}): TestObjectDetailViewModel {
    const latestState = input.states[0];
    const latestFindingAt = input.findings
        .map(finding => finding.createdOn)
        .filter((createdOn): createdOn is DateTime => !!createdOn)
        .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];
    const browserSessionIds = [...new Set((input.states || []).map(state => state.browserSessionId).filter(Boolean))];
    const testRunIds = [...new Set((input.states || []).map(state => state.testRunId).filter((testRunId): testRunId is TestRunId => !!testRunId))];
    const collectionIds = (input.collections || []).map(collection => collection.collectionId);
    const activityStatus = getTestObjectActivityStatus((input.states || []).length > 0, !!input.stateSummaryError);
    const canonicalIdentifier = getTestObjectCanonicalIdentifier(input.testObject);
    const lastCapturedAt = latestState?.creationTime;
    const recentActivityAt = getMostRecentTimestamp([
        lastCapturedAt,
        latestFindingAt,
        input.testObject.lastUpdateTime,
        input.testObject.creationTime
    ]);

    return {
        title: input.testObject.name,
        typeLabel: getTestObjectTypeLabel(input.testObject.origin),
        originLabel: getTestObjectTypeLabel(input.testObject.origin),
        originDescription: getTestObjectOriginDescription(input.testObject.origin),
        activityStatus,
        activityLabel: getTestObjectActivityLabel(activityStatus),
        activityClass: getTestObjectActivityClass(activityStatus),
        canonicalIdentifier,
        createdAt: input.testObject.creationTime,
        updatedAt: input.testObject.lastUpdateTime,
        lastCapturedAt,
        lastCapturedLabel: lastCapturedAt ? formatRelativeTime(lastCapturedAt) : 'Not available',
        collectionsCount: input.collections?.length ?? 0,
        statesCount: input.states?.length ?? 0,
        findingsCount: input.findingsCount ?? input.findings?.length ?? 0,
        sessionsCount: browserSessionIds.length,
        recentActivityAt,
        recentActivityLabel: buildRecentActivityLabel({
            lastCapturedAt,
            latestFindingAt,
            lastUpdatedAt: input.testObject.lastUpdateTime,
            createdAt: input.testObject.creationTime
        }),
        authoredProvenanceLabel: getAuthoredProvenanceLabel(input.testObject.origin),
        testRunIds,
        browserSessionIds,
        collectionIds
    };
}

export function buildTestObjectSeveritySummary(
    findings: Array<{severity?: string}>,
    aggregations?: TestReportQueryAggregation[]
): TestObjectSeveritySummary[] {
    const aggregationBuckets = aggregations?.find(aggregation => aggregation.name === 'severity')?.buckets || [];
    const counts: Partial<Record<FindingSeverity, number>> = aggregationBuckets.length > 0
        ? aggregationBuckets.reduce((acc, bucket) => {
            const severity = normalizeSeverity(bucket.key);
            acc[severity] = (acc[severity] || 0) + bucket.count;
            return acc;
        }, {} as Partial<Record<FindingSeverity, number>>)
        : (findings || []).reduce((acc, finding) => {
            const severity = normalizeSeverity(finding.severity);
            acc[severity] = (acc[severity] || 0) + 1;
            return acc;
        }, {} as Partial<Record<FindingSeverity, number>>);

    return (Object.keys(counts) as FindingSeverity[])
        .map(severity => ({
            severity,
            count: counts[severity] || 0,
            label: `${SEVERITY_LABELS[severity]} ${counts[severity] || 0}`
        }))
        .filter(summary => summary.count > 0)
        .sort((left, right) => (SEVERITY_ORDER[left.severity] ?? 99) - (SEVERITY_ORDER[right.severity] ?? 99));
}

export function buildTestObjectFindingsSectionViewModel(input: {
    latestState?: TestObjectStateSummary;
    currentAvailability: TestObjectCurrentFindingsAvailability;
    currentFindings?: TestObjectRelatedFinding[];
    currentAggregations?: TestReportQueryAggregation[];
    historicalFindings?: TestObjectRelatedFinding[];
    historicalAggregations?: TestReportQueryAggregation[];
    historicalTotalCount?: number;
}): TestObjectFindingsSectionViewModel {
    const currentBreakdown = input.currentAvailability === 'available'
        ? buildTestObjectSeveritySummary(input.currentFindings || [], input.currentAggregations)
        : [];
    const historicalBreakdown = buildTestObjectSeveritySummary(input.historicalFindings || [], input.historicalAggregations);
    const currentCounts = toSeverityCountMap(currentBreakdown);
    const historicalCounts = toSeverityCountMap(historicalBreakdown);
    const currentTotal = (input.currentFindings || []).length;
    const historicalTotal = input.historicalTotalCount ?? input.historicalFindings?.length ?? 0;

    return {
        helperText: 'Current and historical findings for this Test Object',
        currentHeadline: buildCurrentHeadline(input.currentAvailability, currentCounts, currentTotal),
        currentSupportingText: buildCurrentSupportingText(input.currentAvailability, currentCounts, currentTotal, historicalCounts, historicalTotal),
        currentScopeLabel: 'Latest state',
        currentBreakdown,
        currentEmptyLabel: input.currentAvailability === 'available'
            ? 'No findings linked to the latest state'
            : 'Latest-state findings unavailable',
        currentUnavailable: input.currentAvailability !== 'available',
        historicalScopeLabel: 'Across project history',
        historicalBreakdown,
        historicalTotalLabel: `${historicalTotal} linked ${historicalTotal === 1 ? 'finding' : 'findings'}`,
        preview: selectTestObjectFindingPreview(input.currentFindings || [], input.historicalFindings || [])
    };
}

export function getFindingSeverityDisplayLabel(severity?: string): string {
    return SEVERITY_LABELS[normalizeSeverity(severity)];
}

export function getTestObjectActivityStatus(hasStates: boolean, hasStateSummaryError: boolean): TestObjectOverviewActivityStatus {
    if (hasStateSummaryError) {
        return 'status_unavailable';
    }

    return hasStates ? 'observed' : 'no_states';
}

export function getTestObjectActivityLabel(status: TestObjectOverviewActivityStatus): string {
    switch (status) {
        case 'observed':
            return 'Observed';
        case 'status_unavailable':
            return 'Status unavailable';
        default:
            return 'No states yet';
    }
}

export function getTestObjectActivityTooltip(status?: TestObjectOverviewActivityStatus): string {
    switch (status) {
        case 'observed':
            return 'At least one state is directly linked to this Test Object.';
        case 'status_unavailable':
            return 'State information could not be loaded for this Test Object.';
        default:
            return 'No directly linked states are available for this Test Object yet.';
    }
}

export function getTestObjectActivityClass(status: TestObjectOverviewActivityStatus): string {
    switch (status) {
        case 'observed':
            return 'is-observed';
        case 'status_unavailable':
            return 'is-unavailable';
        default:
            return 'is-empty';
    }
}

export function getLastSeenLabel(lastSeenAt?: string, activityStatus?: TestObjectOverviewActivityStatus): string {
    if (lastSeenAt) {
        return `Last seen ${formatRelativeTime(lastSeenAt)}`;
    }

    if (activityStatus === 'status_unavailable') {
        return 'Recent activity unavailable';
    }

    return 'Never captured';
}

export function buildRecentActivityLabel(input: {
    lastCapturedAt?: string;
    latestFindingAt?: string;
    lastUpdatedAt?: string;
    createdAt?: string;
}): string {
    const candidates = [
        {label: 'Captured', value: input.lastCapturedAt},
        {label: 'Finding', value: input.latestFindingAt},
        {label: 'Updated', value: input.lastUpdatedAt},
        {label: 'Created', value: input.createdAt}
    ].filter(candidate => !!candidate.value) as Array<{label: string; value: string}>;

    if (candidates.length === 0) {
        return 'No recent activity available';
    }

    const latest = candidates.reduce((current, candidate) => new Date(candidate.value).getTime() > new Date(current.value).getTime() ? candidate : current);
    return `${latest.label} ${formatRelativeTime(latest.value)}`;
}

export function buildCollectionMembershipCountMap(collections: Array<{members: string[]}>): Record<TestObjectId, number> {
    return (collections || []).reduce((counts, collection) => {
        const uniqueIds = new Set((collection.members || []).map(member => member.split('@', 1)[0] as TestObjectId));
        uniqueIds.forEach(testObjectId => {
            counts[testObjectId] = (counts[testObjectId] || 0) + 1;
        });
        return counts;
    }, {} as Record<TestObjectId, number>);
}

export function getDisplayValueOrFallback(value?: string | null, fallback: string = 'Not available'): string {
    const normalizedValue = typeof value === 'string' ? value.trim() : value;
    return normalizedValue ? normalizedValue.toString() : fallback;
}

function buildCurrentHeadline(
    availability: TestObjectCurrentFindingsAvailability,
    counts: Partial<Record<FindingSeverity, number>>,
    total: number
): string {
    if (availability === 'no_state') {
        return 'Current findings unavailable';
    }

    if (availability === 'unavailable') {
        return 'Current findings could not be loaded';
    }

    if ((counts.error || 0) > 0) {
        const errorCount = counts.error || 0;
        return `${errorCount} current ${errorCount === 1 ? 'error' : 'errors'} in latest state`;
    }

    if ((counts.warn || 0) > 0) {
        return 'No current errors in latest state';
    }

    if ((counts.info || 0) > 0) {
        return 'Only informational findings in latest state';
    }

    if ((counts.good || 0) > 0) {
        return 'No current issues in latest state';
    }

    return total > 0 ? 'Current findings available' : 'No findings linked to the latest state';
}

function buildCurrentSupportingText(
    availability: TestObjectCurrentFindingsAvailability,
    currentCounts: Partial<Record<FindingSeverity, number>>,
    currentTotal: number,
    historicalCounts: Partial<Record<FindingSeverity, number>>,
    historicalTotal: number
): string | undefined {
    if (availability === 'no_state') {
        return historicalTotal > 0
            ? 'Showing historical totals only because no linked state is available for a latest-state summary.'
            : 'No linked state is available for a latest-state summary.';
    }

    if (availability === 'unavailable') {
        return historicalTotal > 0
            ? 'Latest-state findings could not be loaded, so only historical totals are shown.'
            : 'Latest-state findings could not be loaded.';
    }

    if ((currentCounts.error || 0) > 0) {
        const warnCount = currentCounts.warn || 0;
        return warnCount > 0 ? `${warnCount} ${warnCount === 1 ? 'warning remains' : 'warnings remain'} in the latest state.` : undefined;
    }

    if ((currentCounts.warn || 0) > 0) {
        const warnCount = currentCounts.warn || 0;
        return `${warnCount} ${warnCount === 1 ? 'warning is' : 'warnings are'} still linked to the latest state.`;
    }

    if ((currentCounts.info || 0) > 0) {
        const infoCount = currentCounts.info || 0;
        return `${infoCount} informational ${infoCount === 1 ? 'finding' : 'findings'} in the latest state.`;
    }

    if ((currentCounts.good || 0) > 0) {
        const historicalErrorCount = historicalCounts.error || 0;
        if (historicalErrorCount > 0) {
            return `Currently clean, but historically had ${historicalErrorCount} ${historicalErrorCount === 1 ? 'error' : 'errors'} across project history.`;
        }
        return `${currentCounts.good} successful ${currentCounts.good === 1 ? 'check' : 'checks'} in the latest state.`;
    }

    if (currentTotal === 0 && historicalTotal > 0) {
        return `Historically had ${historicalTotal} linked ${historicalTotal === 1 ? 'finding' : 'findings'} across project history.`;
    }

    return undefined;
}

function toSeverityCountMap(summaries: TestObjectSeveritySummary[]): Partial<Record<FindingSeverity, number>> {
    return (summaries || []).reduce((acc, summary) => {
        acc[summary.severity] = summary.count;
        return acc;
    }, {} as Partial<Record<FindingSeverity, number>>);
}

function getMostRecentTimestamp(values: Array<string | undefined>): string | undefined {
    return values
        .filter((value): value is string => !!value)
        .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];
}

function normalizeSeverity(value?: string): FindingSeverity {
    switch (value) {
        case 'error':
        case 'warn':
        case 'info':
        case 'good':
        case 'not_set':
            return value;
        default:
            return 'not_set';
    }
}

/**
 * Explicit selection rule for the findings preview:
 * 1. Prefer the highest-severity finding that is linked to the latest state.
 * 2. If no latest-state finding exists, fall back to the most recent related historical finding.
 */
function selectTestObjectFindingPreview(
    currentFindings: TestObjectRelatedFinding[],
    historicalFindings: TestObjectRelatedFinding[]
): TestObjectFindingsPreview | undefined {
    const currentPreview = selectHighestSeverityMostRecentFinding(currentFindings);
    if (currentPreview) {
        return {
            label: 'Top current finding',
            reason: 'Highest severity finding in latest state',
            finding: currentPreview
        };
    }

    const historicalPreview = selectMostRecentFinding(historicalFindings);
    if (historicalPreview) {
        return {
            label: 'Most recent historical finding',
            reason: 'Latest linked finding across project history',
            finding: historicalPreview
        };
    }

    return undefined;
}

function selectHighestSeverityMostRecentFinding(findings: TestObjectRelatedFinding[]): TestObjectRelatedFinding | undefined {
    return [...(findings || [])].sort((left, right) => {
        const leftSeverityOrder = SEVERITY_ORDER[normalizeSeverity(left.severity)];
        const rightSeverityOrder = SEVERITY_ORDER[normalizeSeverity(right.severity)];
        if (leftSeverityOrder !== rightSeverityOrder) {
            return leftSeverityOrder - rightSeverityOrder;
        }

        return new Date(right.createdOn || 0).getTime() - new Date(left.createdOn || 0).getTime();
    })[0];
}

function selectMostRecentFinding(findings: TestObjectRelatedFinding[]): TestObjectRelatedFinding | undefined {
    return [...(findings || [])].sort((left, right) => new Date(right.createdOn || 0).getTime() - new Date(left.createdOn || 0).getTime())[0];
}

export function formatRelativeTime(value: string | Date, now: number = Date.now()): string {
    const timestamp = new Date(value).getTime();
    if (!timestamp) {
        return 'recently';
    }

    const diffMs = Math.max(0, now - timestamp);
    const minute = 60_000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diffMs < minute) {
        return 'just now';
    }
    if (diffMs < hour) {
        return `${Math.floor(diffMs / minute)}m ago`;
    }
    if (diffMs < day) {
        return `${Math.floor(diffMs / hour)}h ago`;
    }
    if (diffMs < 7 * day) {
        return `${Math.floor(diffMs / day)}d ago`;
    }

    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }).format(new Date(timestamp));
}
